<template>
    <v-dialog :model-value="visible" persistent max-width="720" @update:model-value="$emit('update:visible', $event)">
        <v-card>
            <v-card-title class="text-wrap">{{ $t("reservations.scheduleEditTitle") }}</v-card-title>
            <v-card-text class="pt-2">
                <v-form ref="formRef">
                    <DividerTitle :title="$t('reservation.loadingSection')" />
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <FormDate
                                ref="loadingDateField"
                                v-model="loadingSchedule"
                                data-cy-prefix="card-schedule-loading-date"
                                single
                                density="compact"
                                :min="loadingDateBounds.min"
                                :max="loadingDateBounds.max"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <FormTime
                                v-model="loadingSchedule"
                                data-cy-prefix="card-schedule-loading-time"
                                required
                                density="compact"
                                :min="loadingTimeBounds.min"
                                :max="loadingTimeBounds.max"
                            />
                        </v-col>
                    </v-row>

                    <DividerTitle :title="$t('reservation.unloadingSection')" />
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <FormDate
                                ref="unloadingDateField"
                                v-model="unloadingSchedule"
                                data-cy-prefix="card-schedule-unloading-date"
                                single
                                density="compact"
                                :min="unloadingDateBounds.min"
                                :max="unloadingDateBounds.max"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <FormTime
                                v-model="unloadingSchedule"
                                data-cy-prefix="card-schedule-unloading-time"
                                required
                                density="compact"
                                :min="unloadingTimeBounds.min"
                                :max="unloadingTimeBounds.max"
                            />
                        </v-col>
                    </v-row>

                    <v-textarea
                        v-model="changeReason"
                        class="mt-4"
                        variant="outlined"
                        density="compact"
                        rows="3"
                        hide-details="auto"
                        :rules="changeReasonRules"
                        :label="$t('reservations.scheduleChangeReason')"
                    />
                </v-form>
            </v-card-text>
            <v-card-actions class="px-4 pb-4 pt-0">
                <v-row dense class="w-100 ma-0">
                    <v-col cols="6" class="pa-1">
                        <v-btn block variant="flat" color="error" @click="$emit('update:visible', false)">
                            {{ $t("reservation.cancel") }}
                        </v-btn>
                    </v-col>
                    <v-col cols="6" class="pa-1">
                        <v-btn block variant="flat" color="primary" :loading="submitting" @click="submitSchedulePatch">
                            {{ $t("common.save") }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from "axios";
import DividerTitle from "@/components/forms/DividerTitle.vue";
import FormDate from "@/components/forms/FormDate.vue";
import FormTime from "@/components/forms/FormTime.vue";
import { formatToHhMm, formatToYmd } from "@/utils";

export default {
    name: "ReservationScheduleEditModal",
    components: { DividerTitle, FormDate, FormTime },
    props: {
        visible: { type: Boolean, default: false },
        reservation: { type: Object, required: true },
        entryType: { type: String, required: true },
        isEntryOwner: { type: Boolean, required: true },
    },
    emits: ["update:visible", "saved", "error"],
    data() {
        return {
            submitting: false,
            changeReason: "",
            loadingSchedule: {
                date: [],
                dateStart: "",
                dateEnd: "",
                timeStart: "",
                timeEnd: "",
            },
            unloadingSchedule: {
                date: [],
                dateStart: "",
                dateEnd: "",
                timeStart: "",
                timeEnd: "",
            },
            changeReasonRules: [(value) => !!(value && String(value).trim()) || this.$t("validation.required")],
        };
    },
    computed: {
        wareTransporterEditingOwnReservation() {
            return this.entryType === "ware" && !this.isEntryOwner;
        },
        loadingEntryWindow() {
            return this.reservation?.entry_summary?.entry_loading_window || {};
        },
        unloadingEntryWindow() {
            return this.reservation?.entry_summary?.entry_unloading_window || {};
        },
        loadingDateBounds() {
            if (this.wareTransporterEditingOwnReservation) {
                return {
                    min: this.loadingEntryWindow.date_start_iso || null,
                    max: this.loadingEntryWindow.date_end_iso || null,
                };
            }
            return { min: null, max: null };
        },
        unloadingDateBounds() {
            if (this.wareTransporterEditingOwnReservation) {
                return {
                    min: this.unloadingEntryWindow.date_start_iso || null,
                    max: this.unloadingEntryWindow.date_end_iso || null,
                };
            }
            return { min: null, max: null };
        },
        loadingTimeBounds() {
            if (this.wareTransporterEditingOwnReservation) {
                const startText = this.loadingEntryWindow.time_start || "";
                const endText = this.loadingEntryWindow.time_end || "";
                if (startText && endText) {
                    return { min: startText, max: endText };
                }
            }
            return { min: null, max: null };
        },
        unloadingTimeBounds() {
            if (this.wareTransporterEditingOwnReservation) {
                const startText = this.unloadingEntryWindow.time_start || "";
                const endText = this.unloadingEntryWindow.time_end || "";
                if (startText && endText) {
                    return { min: startText, max: endText };
                }
            }
            return { min: null, max: null };
        },
    },
    watch: {
        visible: {
            immediate: true,
            handler(nextVisible) {
                if (nextVisible) {
                    this.changeReason = "";
                    this.prefillFromReservation();
                }
            },
        },
        "reservation.uid"() {
            if (this.visible) this.prefillFromReservation();
        },
    },
    methods: {
        prefillFromReservation() {
            const reservationData = this.reservation || {};
            const loadingIso = this.isoPrefixFromValue(reservationData.chosen_loading_date);
            const unloadingIso = this.isoPrefixFromValue(reservationData.chosen_unloading_date);

            this.loadingSchedule = {
                ...this.dateAndTimesFromIso(
                    loadingIso,
                    reservationData.chosen_loading_time_start,
                    reservationData.chosen_loading_time_end
                ),
            };
            this.unloadingSchedule = {
                ...this.dateAndTimesFromIso(
                    unloadingIso,
                    reservationData.chosen_unloading_time_start,
                    reservationData.chosen_unloading_time_end
                ),
            };
        },
        isoPrefixFromValue(value) {
            if (value == null || value === "") return "";
            const text = String(value).trim();
            return text.length >= 10 ? text.slice(0, 10) : "";
        },
        dateAndTimesFromIso(isoPrefix, timeStartRaw, timeEndRaw) {
            const base = {
                date: [],
                dateStart: "",
                dateEnd: "",
                timeStart: formatToHhMm(timeStartRaw) || "",
                timeEnd: formatToHhMm(timeEndRaw) || "",
            };
            if (!isoPrefix || isoPrefix.length < 10) return base;
            const dateObject = new Date(`${isoPrefix}T12:00:00`);
            if (Number.isNaN(dateObject.getTime())) return base;
            return {
                date: [dateObject],
                dateStart: isoPrefix,
                dateEnd: isoPrefix,
                timeStart: base.timeStart,
                timeEnd: base.timeEnd,
            };
        },
        async submitSchedulePatch() {
            const formValidation = await this.$refs.formRef?.validate();
            if (!formValidation?.valid) return;

            const loadingDateOk = await this.$refs.loadingDateField?.validateFields?.();
            const unloadingDateOk = await this.$refs.unloadingDateField?.validateFields?.();
            if (!loadingDateOk || !unloadingDateOk) return;

            const loadingDateIso =
                formatToYmd(this.loadingSchedule.date?.[0]) || this.loadingSchedule.dateStart || "";
            const unloadingDateIso =
                formatToYmd(this.unloadingSchedule.date?.[0]) || this.unloadingSchedule.dateStart || "";

            if (
                !loadingDateIso ||
                !unloadingDateIso ||
                !this.loadingSchedule.timeStart ||
                !this.loadingSchedule.timeEnd ||
                !this.unloadingSchedule.timeStart ||
                !this.unloadingSchedule.timeEnd
            ) {
                return;
            }

            const trimmedReason = String(this.changeReason || "").trim();
            if (!trimmedReason) return;

            this.submitting = true;
            try {
                await axios.patch(
                    `${process.env.VUE_APP_API_URL}/reservations/${this.reservation.uid}/schedule/`,
                    {
                        chosen_loading_date: loadingDateIso,
                        chosen_loading_time_start: this.loadingSchedule.timeStart,
                        chosen_loading_time_end: this.loadingSchedule.timeEnd,
                        chosen_unloading_date: unloadingDateIso,
                        chosen_unloading_time_start: this.unloadingSchedule.timeStart,
                        chosen_unloading_time_end: this.unloadingSchedule.timeEnd,
                        change_reason: trimmedReason,
                    }
                );
                this.$emit("update:visible", false);
                this.$emit("saved");
            } catch (error) {
                const errorCode = error.response?.data?.error;
                this.$emit("error", errorCode || "schedule_patch_failed");
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>
